import React from "react";
import { Button } from "@mui/material";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { EditPencil } from "assets";
import { DimensionAllocation, EmissionsAllocationDetails, Site, updateEmissionsAllocation, UpdateEmissionsAllocationRequestBody } from "api";
import { useNotify } from "hooks";
import { calculateTotalEmissions } from "../../helpers";

interface ConfigurationButtonProps {
    filteredSites: Site[];
    allocationId: number;
    onEditMode: boolean;
    setOnEditMode: (val: boolean) => void;
    setIsLoading: (val: boolean) => void;
    dimensionValuesChanged: DimensionAllocation[];
    setDimensionValuesChanged: (val: DimensionAllocation[]) => void;
    disabled?: boolean;
}

export const ConfigurationButton = ({ onEditMode, setOnEditMode, setIsLoading, filteredSites, allocationId, dimensionValuesChanged, setDimensionValuesChanged, disabled = false }: ConfigurationButtonProps) => {
    const notify = useNotify();
    const queryClient = useQueryClient();

    const { mutateAsync: updateEmissions } = useMutation<
        EmissionsAllocationDetails,
        AxiosError,
        UpdateEmissionsAllocationRequestBody
    >({
        mutationFn: updateEmissionsAllocation,
        onMutate: () => {
            setIsLoading(true);
        },
        onSuccess: () => {
            notify.success("Emission Allocation successfully updated.");
            setDimensionValuesChanged([]);
            queryClient.invalidateQueries(['emissionsAllocationDetails', allocationId])
            setIsLoading(false);
        },
        onError: (error: AxiosError) => {
            setIsLoading(false);
            notify.error(typeof error.response?.data === 'string' ? error.response?.data : 'Failed to update emissions allocation');
        },
    });

    const saveChanges = async () => {
        if (dimensionValuesChanged.length > 0) {
            try {
                const requestBody = {
                    allocationId,
                    dimensions: dimensionValuesChanged
                }
                await updateEmissions(requestBody);
            } catch (error: any) {
                notify.error(error.response?.data as string || 'Failed to create emissions allocation');
            }

        }
    };

    const onConfigurationButtonClick = () => {
        if (onEditMode) {
            const invalidTotals = filteredSites.filter((site) => {
                const total = calculateTotalEmissions(site);
                return total !== 0 && total !== 100;
            });
            if (invalidTotals.length > 0) {
                notify.error("Some of the dimensions don't currently add up to 100%.\t Please review the data and ensure everything totals 100% before saving.")
                return;
            }
            saveChanges();
        }
        setOnEditMode(!onEditMode);
    };

    return <Button
        variant="contained"
        startIcon={<EditPencil />}
        sx={{ borderRadius: "8px", width: '200px', background: '#191FDB' }}
        onClick={onConfigurationButtonClick}
        disabled={disabled}
        data-testid="configuration-btn"
    >
        {onEditMode ? "Save Configuration" : "Edit Configuration"}
    </Button>
}